import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Lock, Save, Loader2, User as UserIcon } from 'lucide-react';
import { authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const UserProfile = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (pwd.newPassword !== pwd.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSavingPwd(true);
    try {
      await authAPI.changePassword(pwd);
      toast.success(t('profile.passwordChanged'));
      setPwd({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || t('errors.serverError'));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.title')}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col items-center">
          <div className="w-32 h-32 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
            <UserIcon size={64} />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{user?.firstName} {user?.lastName}</h2>
          <p className="text-sm text-gray-500 mt-1">{user?.username}</p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{t('profile.changePassword')}</h2>
          </div>
          <form onSubmit={handlePasswordSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.currentPassword')}</label>
              <input
                type="password"
                value={pwd.currentPassword}
                onChange={(e) => setPwd({ ...pwd, currentPassword: e.target.value })}
                className="form-input"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.newPassword')}</label>
                <input
                  type="password"
                  value={pwd.newPassword}
                  onChange={(e) => setPwd({ ...pwd, newPassword: e.target.value })}
                  className="form-input"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.confirmPassword')}</label>
                <input
                  type="password"
                  value={pwd.confirmPassword}
                  onChange={(e) => setPwd({ ...pwd, confirmPassword: e.target.value })}
                  className="form-input"
                  required
                  minLength={6}
                />
              </div>
            </div>
            <button type="submit" disabled={savingPwd} className="btn-primary">
              {savingPwd ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              <span>{t('profile.saveChanges')}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;